import { useEffect } from 'react';
import { Card, Checkbox, Heading, Inline, RadioCard, RadioGroup, Stack, Text, textVariants, ValidationMessage, vars } from '@etg/wings';
import { css, cx, mediaQueries } from '@eti/styles';
import { useTranslation, useSiteContext } from '@eti/providers';
import { Pictogram, DangerousHtmlContent, Illustration } from '@eti/components';
import Media from 'react-media';
import checkIcon from '@eti/assets/icons/checkList.svg?url'; // eslint-disable-line import/no-unresolved

import { screenReaderOnly } from '../../common/utils/styles';
import { useDynamicIcon } from '../../common/utils/dynamic-icons/useDynamicIcon';
import { DYNAMIC_ICON } from '../../common/utils/dynamic-icons/icons';
import { useGetData } from '../utils';
import ReadMore from './ReadMore';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const wrapperStyles = iconContent => css`
  a {
    position: relative;

    &[target='_blank']::after {
      content: ${iconContent};
      margin-inline-start: 4px;
      position: relative;
      top: 5px;
    }
  }
`;
const titleAndPictogramStyles = css`
  margin-bottom: 2px;
  margin-top: 16px;
`;
const descriptionStyles = css`
  font-size: 1rem;
`;
const priceTagStyles = css`
  font-size: 1.25rem;
  font-weight: 700;
`;
const productDescriptionStyles = css`
  line-height: 150%;
  padding-inline-start: 58px;

  & ul {
    margin-top: 8px;
  }

  & li {
    padding-inline-start: 24px;
    position: relative;

    &::before {
      background: url(${checkIcon});
      background-repeat: no-repeat;
      background-size: contain;
      content: '';
      display: inline-block;
      height: 16px;
      inset-inline-start: 0;
      margin-inline-end: 8px;
      position: absolute;
      top: 3px;
      width: 16px;
    }
  }

  li:not(:last-child) {
    margin-bottom: 8px;
  }
`;
const MutuallyExclusive = _ref => {
  let {
    className,
    emptyOptionId: emptyOptionCheckBoxId,
    input,
    isAnyProductSelectedInBundle,
    meta,
    products,
    selection
  } = _ref;
  const {
    t
  } = useTranslation();
  const {
    brand: {
      code: brand
    }
  } = useSiteContext();
  const {
    touched: isTouched,
    error: errorMessage
  } = meta;
  const {
    onChange
  } = input;
  const hasError = Boolean(isTouched && errorMessage);
  const productsData = useGetData(products);
  const [icon] = useDynamicIcon(vars.colors.links.default, DYNAMIC_ICON.linkIcon);
  useEffect(() => {
    if (isAnyProductSelectedInBundle) {
      onChange(emptyOptionCheckBoxId);
    }
  }, [isAnyProductSelectedInBundle, onChange, emptyOptionCheckBoxId]);
  if (isAnyProductSelectedInBundle) {
    return null;
  }
  return /*#__PURE__*/_jsx(Card, {
    className: cx(wrapperStyles(icon), className),
    "data-testid": "mutually-exclusive-container",
    children: /*#__PURE__*/_jsxs(Stack, {
      spacing: 16,
      children: [/*#__PURE__*/_jsx(Media, {
        query: mediaQueries.medium.up,
        children: matches => /*#__PURE__*/_jsxs(Inline, {
          spacing: 16,
          children: [matches && /*#__PURE__*/_jsx(Illustration, {
            className: css`
                    width: 126px;
                  `,
            id: "insurance-small"
          }), /*#__PURE__*/_jsxs(Stack, {
            className: css('flex: 1;'),
            spacing: 8,
            children: [/*#__PURE__*/_jsxs(Inline, {
              className: titleAndPictogramStyles,
              spacing: 8,
              children: [!matches && /*#__PURE__*/_jsx(Pictogram, {
                brand: brand,
                className: css('margin-top:4px;'),
                id: "bankruptcy-insurance",
                size: "32px"
              }), /*#__PURE__*/_jsx(Heading, {
                "data-testid": "mutually-exclusive-title",
                level: 2,
                children: t('Products.MutuallyExclusive.Title')
              })]
            }), /*#__PURE__*/_jsx("div", {
              className: descriptionStyles,
              "data-testid": "mutually-exclusive-description",
              children: t('Products.MutuallyExclusive.Description')
            })]
          })]
        })
      }), /*#__PURE__*/_jsxs(RadioGroup, {
        className: css('label {font-weight: 700;}'),
        hasError: hasError,
        name: "cardSectionChildrenExample",
        onChange: e => onChange(e.target.value),
        selectedValue: selection,
        children: [/*#__PURE__*/_jsx(RadioGroup.Legend, {
          className: screenReaderOnly,
          children: t('Products.MutuallyExclusive.Description')
        }), /*#__PURE__*/_jsxs(Stack, {
          spacing: 16,
          children: [hasError && errorMessage && /*#__PURE__*/_jsx(ValidationMessage, {
            "data-testid": "mutually-exclusive-error-message",
            children: errorMessage
          }), productsData === null || productsData === void 0 ? void 0 : productsData.map(_ref2 => {
            let {
              productTitle,
              productDescription,
              productReadMore,
              productId,
              productPrice
            } = _ref2;
            return /*#__PURE__*/_jsxs(RadioCard, {
              "data-offer-rate": productId,
              "data-testid": `mutually-exclusive-option-${productId}`,
              id: `mutually-exclusive-option-${productId}`,
              label: /*#__PURE__*/_jsx(Text, {
                variant: textVariants._18_EMPHASIZED,
                children: productTitle
              }),
              value: String(productId),
              children: [/*#__PURE__*/_jsx(Card.Section, {
                className: productDescriptionStyles,
                children: /*#__PURE__*/_jsxs(Stack, {
                  spacing: 8,
                  children: [/*#__PURE__*/_jsx(DangerousHtmlContent, {
                    content: productDescription,
                    "data-testid": `mutually-exclusive-abstract-text-${productId}`
                  }), productReadMore && /*#__PURE__*/_jsx(ReadMore, {
                    content: productReadMore,
                    productId: productId
                  })]
                })
              }), /*#__PURE__*/_jsx(Card.Section, {
                children: /*#__PURE__*/_jsxs(Inline, {
                  align: "end",
                  alignY: "center",
                  children: [t('Products.Price.Label.Only'), /*#__PURE__*/_jsx("span", {
                    className: priceTagStyles,
                    "data-testid": `mutuallyExclusive-${productId}-price`,
                    children: productPrice
                  })]
                })
              })]
            }, productId);
          })]
        })]
      }), /*#__PURE__*/_jsxs(Inline, {
        align: "end",
        "data-testid": "mutually-exclusive-empty-option",
        spacing: 16,
        children: [/*#__PURE__*/_jsx(Checkbox.Label, {
          htmlFor: emptyOptionCheckBoxId,
          children: t('Product.Bundle.Selection.Empty.Option.Label')
        }), /*#__PURE__*/_jsx(Checkbox, {
          checked: selection === emptyOptionCheckBoxId,
          hasError: hasError,
          id: emptyOptionCheckBoxId,
          onChange: () => onChange(emptyOptionCheckBoxId),
          value: emptyOptionCheckBoxId
        })]
      })]
    })
  });
};
export default MutuallyExclusive;
//# sourceMappingURL=MutuallyExclusive.js.map